

























































































































































import {
  defineComponent,
  useFetch,
  ref,
  computed,
  useRouter,
  useRoute,
  useContext,
  onMounted,
  watch,
} from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import sanitizeHtml from 'sanitize-html';
import urlJoin from 'url-join';
import AppAvatar from '~/components/partials/App/AppAvatar.vue';
import AppContentFavoriteIconButton from '~/components/partials/App/AppContentFavoriteIconButton.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppRelatedContents from '~/components/partials/App/AppRelatedContents.vue';
import ContentArticleSideMenu from '~/components/partials/Content/ContentArticleSideMenu.vue';
import ContentDynamicComponent from '~/components/partials/Content/ContentDynamicComponent.vue';
import ContentSearchInput from '~/components/partials/Content/ContentSearchInput.vue';
import ContentTagsAndShareIcons from '~/components/partials/Content/ContentTagsAndShareIcons.vue';
import ContentLayout from '~/components/partials/Layout/ContentLayout.vue';
import PopularArticles from '~/components/partials/SilverEgg/PopularArticles.vue';
import RecommendArticles from '~/components/partials/SilverEgg/RecommendArticles.vue';
import { useListFavorite } from '~/composables/contents/useListFavorite';
import { useRequestBody } from '~/composables/contents/useRequestBody';
import { useTags } from '~/composables/contents/useTags';
import { useSilverEggApi } from '~/composables/silverEgg/useSilverEggApi';
import { useUuidEncryption } from '~/composables/silverEgg/useUuidEncryption';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useMatchMedia } from '~/composables/useMatchMedia';
import { useRuntimeConfig } from '~/composables/useRuntimeConfig';
import { useStore } from '~/composables/useStore';
import {
  SPEC,
  SMARTPHONE,
  REQUEST_NUM,
  CATEGORY_ID,
  DYNAMIC_PARTS_NAME,
  REQUEST_NUM_ARTICLE_RANKING,
} from '~/constants';
import { url, karteTrack, nonNullable } from '~/utils';
import { PageError } from '~/utils/error';
import { RecommendItem } from '~/types/common/silverEgg';
import { ArticleCategory, EditorProfile, Tags } from '~/types/modules/contents';
import { FavoriteTypes } from '~/enums';

export default defineComponent({
  components: {
    AppAvatar,
    ContentDynamicComponent,
    ContentTagsAndShareIcons,
    AppImage,
    ContentLayout,
    ContentArticleSideMenu,
    ContentSearchInput,
    AppIcon,
    AppLink,
    AppRelatedContents,
    AppContentFavoriteIconButton,
    RecommendArticles,
    PopularArticles,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { FRONTEND_ORIGIN } = useRuntimeConfig();
    const { fetchAllFavorites, checkIsFavorite } = useListFavorite();
    const { fetchRecommendItems } = useSilverEggApi();
    const { encryptUuid } = useUuidEncryption();
    const { parseRequestBody } = useRequestBody();
    const { i18n, req, app } = useContext();
    const { mappedTags } = useTags();
    const { isLargeDesktop } = useMatchMedia();

    const data = ref<any>();
    const author = ref<EditorProfile | null>(null);
    const categories = ref<ArticleCategory[]>([]);
    const tags = ref<Tags>([]);

    const searchString = ref(''); // 入力中の文字
    const isPreview = ref(req && req.method === 'POST');

    // シルバーエッグ用
    const isMobile = ref(app.$ua.deviceType() === SMARTPHONE);
    const popularArticles = ref<RecommendItem[] | undefined>([]);
    const popularRqid = ref('');
    const popularSpec = ref(isMobile.value ? SPEC.ARTICLE_RANKING_DETAIL_SP : SPEC.ARTICLE_RANKING_DETAIL_PC);
    const recommendArticles = ref<RecommendItem[] | undefined>([]);
    const recommendRqid = ref('');
    const recommendSpec = ref(isMobile.value ? SPEC.ARTICLE_DETAIL_SP : SPEC.ARTICLE_DETAIL_PC);
    const uuidHash = ref<string | undefined>('');

    const isFavorite = computed(() => {
      if (!data.value) {
        return false;
      }
      return checkIsFavorite(data.value.article_id.toString(), FavoriteTypes.ARTICLE);
    });

    // 末尾スラッシュがなければスラッシュありの canonical を返す
    const canonical = computed((): string | undefined => {
      const { path, query } = route.value;

      if (!path.endsWith('/')) {
        return `${path}/`;
      }

      if (Object.keys(query).length > 0) {
        return path;
      }
    });

    const { fetchState } = useFetch(async () => {
      let permalink = '';
      // プレビューの表示
      if (isPreview.value) {
        const reqBody = await parseRequestBody(req);
        if (!reqBody) throw new PageError(404, {});

        data.value = reqBody;
        permalink = data.value.permalink;
      } else {
        permalink = route.value.params.permalink;
      }

      if (!permalink) {
        throw new PageError(404, {});
      }

      await Promise.all(
        [
          async () => {
            if (isPreview.value) return;

            const { result } = await store.dispatch('modules/content/fetchMagazineDetail', {
              permalink,
            });

            data.value = result[0];
          },
          async () => {
            const result = await store.dispatch('modules/content/fetchArticleCategories', {});

            categories.value = result;
          },
        ].map((fn) => fn())
      );

      if (!data.value) throw new PageError(404, {});

      const authorRes = await store.dispatch('modules/content/fetchAuthors', {
        authorIds: [Number(data.value.contents?.author_id)],
      });
      author.value = authorRes[0];

      tags.value = (await mappedTags(data.value.contents?.tags, [])) || [];
    });

    onMounted(() => {
      // karteの送信処理には全件取得する必要があります
      fetchAllFavorites(FavoriteTypes.ARTICLE);
    });

    watch(
      () => data.value?.article_id,
      async () => {
        if (isPreview.value) return;

        window.TLAB.push(['Beacon::PutAccessLog', [`article_${data.value.article_id}`]]);

        // シルバーエッグのレコメンド枠リクエスト&トラッキング
        if (app.$ua.isFromCrawler()) return;
        // 記事ランキング
        const rankingRes = await fetchRecommendItems({
          spec: popularSpec.value,
          num: REQUEST_NUM_ARTICLE_RANKING,
          cat: CATEGORY_ID.ARTICLE,
        });
        popularArticles.value = rankingRes.items;
        popularRqid.value = rankingRes.rqid;
        // サイドメニュー(xlが分岐点)に表示するときのみ、SPサムネ画像に差し替え
        if (isLargeDesktop.value && popularArticles.value?.length) {
          const images = await store.dispatch('modules/content/fetchArticleSpThumbnails', {
            ids: popularArticles.value.map((item) => item._id),
          });
          popularArticles.value = popularArticles.value.map((item) => {
            const img = images.find((image) => image.id === item._id)?.thumbnailImageSp ?? item.img_url;
            return {
              ...item,
              img_url: img,
            };
          });
        }

        // おすすめ記事
        uuidHash.value = await encryptUuid();
        const recommendRes = await fetchRecommendItems({
          prod: [data.value.article_id], // トラッキング用
          spec: recommendSpec.value,
          num: REQUEST_NUM,
          cat: CATEGORY_ID.ARTICLE,
          cust: uuidHash.value,
        });
        recommendArticles.value = recommendRes.items;
        recommendRqid.value = recommendRes.rqid;
      }
    );

    // ダイナミックパーツのレスポンスを加工する
    const dynamicParts = computed((): any[] => {
      return data.value?.contents?.__dynamic_parts || [];
    });

    // 構造化マークアップで使用するリードテキスト
    const leadText = computed((): string | undefined => {
      const part = dynamicParts.value.find((part) => part.parts === 'body_text');
      try {
        return sanitizeHtml(part.items.text || '', { allowedTags: [] });
      } catch {
        // なにも返さないがハンドリングもしない
      }
    });

    // 見出しパーツ
    const headlines = computed(() => {
      // FIXME: nullではなく空配列に修正してもらえたら!!dynamicParts.items.text_with_option.optionは消して良い
      const filteredHeadlines = data.value?.contents
        ? data.value.contents.__dynamic_parts.filter(
            (dynamicParts: any) =>
              dynamicParts.parts === DYNAMIC_PARTS_NAME.HEADLINE &&
              dynamicParts.items.text_with_option.option.includes(DYNAMIC_PARTS_NAME.TABLE_OF_CONTENTS)
          )
        : [];

      return filteredHeadlines.reduce((acc: any[], cur: any) => {
        const { headline_style, text_with_option } = cur.items;
        const { text } = text_with_option;

        if (headline_style === 'big') {
          acc.push({
            text,
            middles: [],
          });

          return acc;
        }

        if (!acc[acc.length - 1]) {
          acc.push({
            text: '',
            middles: [],
          });
        }

        acc[acc.length - 1].middles.push({
          text,
        });

        return acc;
      }, []);
    });

    /**
     * 日付変換
     * @param date 日付
     */
    const formatDate = (date: string) => {
      return dayjs.utc(date).tz('Asia/Tokyo').format('YYYY.M.D');
    };

    /**
     * フリーワードで検索
     */
    const searchWords = async () => {
      await router.push({
        path: url('ARTICLE_LIST'),
        query: {
          _s: searchString.value ? searchString.value : undefined,
        },
      });
    };

    /**
     * idから特定のカテゴリを見つける
     * @param query クエリ
     */
    const searchCategoryName = (id?: number) => {
      return categories.value.find((category) => category.id === id)?.name || '';
    };

    // KARTE
    const unsubscribe = watch(
      data,
      async (data) => {
        if (isPreview.value || process.server || !data) {
          return;
        }
        unsubscribe();
        const tags: string[] = [];
        try {
          const { result } = await store.dispatch('modules/content/fetchTags', {
            tags: data.contents?.tags || [],
          });
          tags.push(...(result?.map((item) => item?.contents?.tag_name)?.filter(nonNullable) || []));
        } catch {
          // 何もしない
        }
        const publishAt = data.publish_at || null;
        const expireAt = data.expire_at || null;
        karteTrack('_view_category', {
          // 商品一覧自体が存在しない
          category_num: 0,
          category_id: `article${data.article_id || ''}`,
          category_name: data.title,
          category_url: urlJoin(FRONTEND_ORIGIN, route.value.fullPath),
          page_type: i18n.t('Content-05.kartePageType').toString(),
          open_from_dtime: dayjs(publishAt).isValid()
            ? dayjs(publishAt).tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          open_to_dtime: dayjs(expireAt).isValid()
            ? dayjs(expireAt).tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          tags,
        });
      },
      { immediate: true }
    );

    const fullUrl = computed(() => {
      return urlJoin(FRONTEND_ORIGIN, route.value.fullPath);
    });
    //  FaceBookのリンク
    const faceBookLink = computed(() => {
      return `https://www.facebook.com/sharer/sharer.php?u=${fullUrl.value}`;
    });
    // Twitterのリンク
    const twitterLink = computed(() => {
      return `https://twitter.com/intent/tweet?url=${fullUrl.value}`;
    });

    const breadcrumbs = computed(() => {
      const category = categories.value.find((category) => category.id === data.value?.contents.category_id);

      return [
        { title: i18n.t('Content-04.breadcrumbs.title').toString(), to: url('ARTICLE_LIST') },
        {
          title: searchCategoryName(data.value?.contents.category_id),
          to: url('ARTICLE_LIST_CATEGORY', {
            permalink: category?.permalink,
          }),
        },
        { title: data.value?.title, to: null },
      ];
    });

    useLayoutProps(() => ({
      fetchState,
      layout: {
        headerMenu: 'never', // ContentLayoutの中で設置
      },
      breadcrumbs: breadcrumbs.value,
      jsonld: [
        {
          type: 'BlogPosting',
          data: {
            url: url('ARTICLE_DETAIL', { permalink: data.value?.permalink }),
            title: data.value?.title || '',
            images: data.value?.contents?.thumbnail_image_pc ? [data.value?.contents?.thumbnail_image_pc] : undefined,
            publishedAt: data.value?.publish_at || '',
            updatedAt: data.value?.update_at,
            authorName: author.value?.name,
            authorUrl: url('ARTICLE_LIST_AUTHOR', { permalink: author.value?.permalink }),
            description: leadText.value,
          },
        },
      ],
      head: {
        titleTemplate: null,
        title: i18n.t('Content-05.seoTitle', { seoTitle: data.value?.title }).toString(),
        description: data.value?.contents?.meta_description || undefined, // どちらも入力がない場合は、nullが返らないようにundefinedを入力
        canonical: canonical.value,
        noindex: !!data.value?.contents?.is_noindex,
      },
      og: {
        image:
          data.value?.contents?.thumbnail_image_pc || data.value?.contents?.main_image_pc || url('ARTICLE_IMAGE_PATH'),
        description: data.value?.contents?.meta_description || undefined, // どちらも入力がない場合は、nullが返らないようにundefinedを入力
      },
      twitter: {
        card: 'summary_large_image',
      },
    }));

    return {
      data,
      author,
      searchString,
      dynamicParts,
      searchWords,
      formatDate,
      searchCategoryName,
      url,
      headlines,
      categories,
      breadcrumbs,
      tags,
      isFavorite,
      popularArticles,
      popularRqid,
      popularSpec,
      recommendArticles,
      recommendRqid,
      recommendSpec,
      faceBookLink,
      twitterLink,
    };
  },
  head: {},
});
